import { useState } from 'react';
import { api, setToken, type Workspace } from '../lib/api';

/**
 * Close the workspace and, if it was the last one, the account with it.
 *
 * rookeryos.com/privacy promises deletion on request, and for a while the
 * product had no way to honour that. This is the honouring.
 *
 * Deliberately plain and deliberately unpleasant to reach: no confirmation
 * modal that a person can dismiss by reflex, the workspace name typed out by
 * hand, and the password again. The screen says exactly what goes and what does
 * not, because somebody deciding whether to do this needs the truth more than
 * they need reassurance.
 */
export function CloseWorkspace({ workspace }: { workspace: Workspace | null }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!workspace) return null;
  const ready = password.length > 0 && confirm === workspace.slug;

  async function close() {
    setBusy(true); setError(null);
    try {
      await api.closeWorkspace(password, confirm);
      // The token is dead server-side the moment the user row goes; clearing it
      // here is what stops the app rendering a workspace that no longer exists.
      setToken(null);
      // The workspace is gone; the door is where a person goes next.
      window.location.href = 'https://rookeryos.com/';
      window.location.reload();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <section className="danger">
      <h3>Close this workspace</h3>
      {!open ? (
        <>
          <p className="hint">
            Deletes every card, script, render, thumbnail, caption track and uploaded file
            in <b>{workspace.name}</b>, and the files behind them. There is no undo and no
            copy kept.
          </p>
          <button className="btn ghost danger-btn" onClick={() => setOpen(true)}>
            Close workspace…
          </button>
        </>
      ) : (
        <>
          <p className="hint">
            This removes everything in <b>{workspace.name}</b> permanently. If this is your
            only workspace, your account goes with it. Operational logs keep request
            metadata for a short period — everything else is gone.
          </p>
          <label>
            Type <code>{workspace.slug}</code> to confirm
            <input type="text" value={confirm} spellCheck={false} autoComplete="off"
                   onChange={(e) => setConfirm(e.target.value)} />
          </label>
          <label>
            Your password
            <input type="password" value={password} autoComplete="current-password"
                   onChange={(e) => setPassword(e.target.value)} />
          </label>
          {error && <p className="err">{error}</p>}
          <div className="kitactions">
            <button className="btn danger-btn" disabled={!ready || busy}
                    onClick={() => void close()}>
              {busy ? 'Closing…' : 'Close it permanently'}
            </button>
            <button className="btn ghost" disabled={busy}
                    onClick={() => { setOpen(false); setConfirm(''); setPassword(''); setError(null); }}>
              Keep it
            </button>
          </div>
        </>
      )}
    </section>
  );
}
